import { useState, useEffect } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IRadiology } from '../PatientList/api/type';
import RadiologyDialog from './RadiologyDialog';
import ListActionButtons from './ListActionButtons';
import { formatStringToDate } from 'utils/formatting';

interface Props extends FormProps {
  defaultList?: IRadiology[];
}

const RadiologyList = (props: Props) => {
  const { setValue, defaultList } = props;

  const { list, setList } = useTable<IRadiology>();
  const [defaultValue, setDefaultValue] = useState<Partial<IRadiology> | null>(
    null
  );

  useEffect(
    () =>
      setList(
        defaultList?.map((item: any) => {
          const radiologyImages = Array.from(
            { length: 5 },
            (_, i) => item[`radiologyImage${i + 1}`]
          ).filter(item => item);
          return {
            ...item,
            radiologyImages,
          };
        }) ?? []
      ),
    [defaultList, setList]
  );
  useEffect(() => {
    setValue(
      'radiology',
      list.map(item => {
        // 저장할 영상 이미지 파일들을 key 값에 맞게 object 재구성
        const images = item.radiologyImages?.reduce((prev, next, index) => {
          if (!next) return prev;
          return { ...prev, [`radiologyImage${index + 1}`]: next };
        }, {});

        // api 업데이트할 형태로 오브젝트를 저장함
        return {
          ...item,
          ...images,
          radiologyTime: formatStringToDate(item.radiologyTime),
        };
      })
    );
  }, [list, setValue]);

  const onCreateStart = () =>
    setDefaultValue({ radiologyTime: new Date().toString() });
  const onCreateList = (data: IRadiology) => setList(list.concat(data));
  const onUpdateList = (data: IRadiology) =>
    setList(
      list.map(item =>
        item.ptRadiologyNo === data.ptRadiologyNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IRadiology }) => {
    const onUpdate = () => setDefaultValue(row);
    const onDelete = () =>
      setList(list.filter(item => item.ptRadiologyNo !== row.ptRadiologyNo));

    return <ListActionButtons onUpdate={onUpdate} onDelete={onDelete} />;
  };

  const columns: GridColDef[] = [
    { field: 'ptRadiologyNo', headerName: '검사번호' },
    {
      field: 'radiologyTime',
      headerName: '검사시간',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'feeId', headerName: '검사코드' },
    { field: 'feeKor', headerName: '검사명(한)' },
    // {
    //   align: 'right',
    //   field: 'actions',
    //   headerName: '',
    //   renderCell: ({ row }) => <ActionButtons row={row} />,
    // },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography
            variant="h5"
            sx={{
              color: '#555555a4',
            }}
          >
            영상검사결과 리스트
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (추가, 수정, 삭제 시 스마트널스 ENR로 문의해주세요.)
          </Typography>

          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            추가
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptRadiologyNo }))}
        />
      </Stack>

      {defaultValue && (
        <RadiologyDialog
          isUpdate={defaultValue.ptRadiologyNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptRadiologyNo === no) ? true : false
          }
          onSave={defaultValue.ptRadiologyNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default RadiologyList;
